import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography, Button,
  Grid,
  Card,
  TextField,
  MenuItem,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from "@mui/material";
import founderImage from "../../../images/SirImgAboutFounder.png"
import { useLocation, useNavigate } from "react-router-dom";
import { usePermission } from "../../../permissions/usePermission";
import dummyImage from "../../../images/user.png";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { getPublicS3Url } from "../../../utils/s3PublicUrl";
import { exportNetworkMembersCsv } from "./networkExport";

import CallIcon from "@mui/icons-material/Call";
import CallFollowUpModal from "./CallFollowUpModal";
import { setNetworkRestoreOnce } from "./networkStateKeys";

const getMemberYear = (member) => {
  const rawDate = member?.joiningDate || member?.updatedAt;
  if (!rawDate) return "";

  const date = new Date(rawDate);
  return Number.isNaN(date.getTime()) ? "" : String(date.getFullYear());
};

const extractStringId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value.$oid === "string") return value.$oid;
    if (value._id) return extractStringId(value._id);
  }
  return "";
};

const getMemberProfileUserId = (member) =>
  extractStringId(member?.userId) ||
  extractStringId(member?.user?._id) ||
  extractStringId(member?.user) ||
  extractStringId(member?._id);

const getNamePart = (member, field) =>
  member?.[field] ||
  member?.user?.[field] ||
  member?.userId?.[field] ||
  member?.userObjectId?.[field] ||
  "";

const getSalutation = (member) =>
  getNamePart(member, "salutation") || getNamePart(member, "Salutation");

const getFullName = (member) =>
  [
    getSalutation(member),
    getNamePart(member, "userFirstName"),
    getNamePart(member, "userMiddleName"),
    getNamePart(member, "userLastName"),
  ]
    .filter(Boolean)
    .join(" ");

const NEC_FILTERS_STORAGE_KEY = "network_filters_nec";

const NEC = ({ members = [], allowScopedRestore = false, canOpenMemberProfile = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const { canByKey } = usePermission();
  const canDownloadExcel = canByKey(
    "COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_DOWNLOAD_EXCEL",
  );
  const navigateFrom = location.pathname.includes("committeeMembersList")
    ? "committeeMembersList"
    : "network";

  const [selectedCommittee, setSelectedCommittee] = useState("ALL");
  const [selectedYear, setSelectedYear] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  useEffect(() => {
    if (!allowScopedRestore) return;
    try {
      const saved = sessionStorage.getItem(NEC_FILTERS_STORAGE_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      setSelectedCommittee(parsed?.selectedCommittee || "ALL");
      setSelectedYear(parsed?.selectedYear || "ALL");
      setSearchTerm(parsed?.searchTerm || "");
      setShowSearch(Boolean(parsed?.showSearch));
    } catch (error) {
      console.warn("Unable to restore NEC filters", error);
    }
  }, [allowScopedRestore]);

  useEffect(() => {
    sessionStorage.setItem(
      NEC_FILTERS_STORAGE_KEY,
      JSON.stringify({
        selectedCommittee,
        selectedYear,
        searchTerm,
        showSearch,
      }),
    );
  }, [selectedCommittee, selectedYear, searchTerm, showSearch]);

  const COMMITTEE_ORDER = [
    "Governing Committee",
    "Operations Committee",
    "National Program Head",
    "State Presidents & State General Secretaries",
  ];

  const COMMITTEE_MAP = {
    "Governing Committee": "Governing Committee",
    "Operational commitee": "Operations Committee",
    "Operations Committee": "Operations Committee",
    "National Program Head": "National Program Head",
    "State Presidents & State General Secretaries":
      "State Presidents & State General Secretaries",
  };

  const getCommitteeStyle = (committee) => {
    const normalizedCommittee = COMMITTEE_MAP[committee] || committee;

    return (
      theme.committeeStyles?.[normalizedCommittee] ||
      theme.committeeStyles?.default
    );
  };

  const years = useMemo(
    () =>
      [...new Set(members.map(getMemberYear).filter(Boolean))].sort(
        (a, b) => Number(b) - Number(a),
      ),
    [members],
  );

  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      const apiCommittee = m?.committeeType;
      const mappedCommittee = COMMITTEE_MAP[apiCommittee];

      const matchCommittee =
        selectedCommittee === "ALL" || mappedCommittee === selectedCommittee;
      const memberYear = getMemberYear(m);
      const matchYear = selectedYear === "ALL" || memberYear === selectedYear;

      const fullName = getFullName(m).toLowerCase();

      const matchSearch = fullName.includes(searchTerm.toLowerCase());

      return matchCommittee && matchYear && matchSearch;
    });
  }, [members, searchTerm, selectedCommittee, selectedYear]);

  const groupedMembers = useMemo(() => {
    const grouped = {};

    COMMITTEE_ORDER.forEach((name) => {
      grouped[name] = [];
    });

    filteredMembers.forEach((member) => {
      const apiCommittee = member?.committeeType;
      const mappedCommittee = COMMITTEE_MAP[apiCommittee];

      if (grouped[mappedCommittee]) {
        grouped[mappedCommittee].push(member);
      }
    });

    return grouped;
  }, [filteredMembers]);

  const formatUpdatedDate = (date) => {
    if (!date) return "";

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getLatestUpdatedAt = (members = []) =>
    members.reduce((acc, member) => {
      const ts = member?.updatedAt || member?.joiningDate;
      if (!ts) return acc;

      const date = new Date(ts);
      if (Number.isNaN(date.getTime())) {
        return acc;
      }

      return !acc || date > acc ? date : acc;
    }, null);

  const visibleUpdatedAt = useMemo(
    () => getLatestUpdatedAt(filteredMembers),
    [filteredMembers],
  );

  const handleExport = () => {
    exportNetworkMembersCsv(filteredMembers, "network_nec_members");
  };

    const FOUNDER = {
      "_id": "6a7585d5a0f828fc003b7463",
      "userImageURL": "public/profile-images/1784114514656-SMsirVMSprofpic (1).png",
      "userFirstName": "Shri Shantilal",
      "userLastName": "Muttha",
      "whatsAppNumber": "9823029525",
      "mobileNumber": "9823029525",
      "gender": "Male",
      "Country": {
        "$oid": "676a5efa74f99238834a5c4c"
      },
      "userRole": "SuperAdmin",
      "verified": "verified",
      "userStatus": "active",
      "filledBy": "System",
      "__v": 1,
      "adminModules": [],
      "updateApprovalStatus": "none",
      position: "Founder",
      committeeType: "Founder",
    }

  return (
    <Box sx={{ px: { xs: 1, sm: 3 }, mt: 3 }}>
      <Box
        sx={{
          display:"flex",
          justifyContent:"space-between",
          flexDirection:{xs:"column", sm:"column", md:"row"},
          gap:2
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: {xs:"column",sm:"column", md:"row"},
            alignItems: "center",
            justifyContent: "space-between",
            width: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              width: {xs: "100%", sm: "100%"},
              flexDirection: {xs:"column", sm:"row"},
            }}
          >
            <TextField
              select
              size="small"
              label="Committee"
              value={selectedCommittee}
              onChange={(e) => setSelectedCommittee(e.target.value)}
              sx={{ minWidth: {xs:"100%", sm:"50%", md: 220} }}
            >
              <MenuItem value="ALL">All</MenuItem>
              {COMMITTEE_ORDER.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ minWidth: {xs:"100%", sm:"50%", md: 220} }}
            >
              <MenuItem value="ALL">All</MenuItem>
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
                  </Box>
        </Box>
        <Box sx={{
            display: "flex",
            flexDirection: {xs:"column",sm:"column", md:"row"},
            alignItems: "flex-end",
            justifyContent: "space-between",
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: isMobile ? "100%" : "auto",
              justifyContent: {xs:"flex-end", sm:"flex-start", md:"flex-end"}
            }}
          >
            {canDownloadExcel && (
              <Tooltip title="Download Excel" arrow>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleExport}
                    disabled={!filteredMembers.length}
                    aria-label="Download Excel"
                    size="small"
                    sx={{
                      textTransform: "none",
                      display:"flex",
                      flexDirection:"row",
                      gap:1
                      //borderRadius: 2,
                    }}
                  >
                    <FileDownloadOutlinedIcon />
                    Download
                  </Button>
                </span>
              </Tooltip>
            )}

            {showSearch ? (
              <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                sx={{ width: isMobile ? "100%" : 220 }}
              />
            ) : (
              <IconButton onClick={() => setShowSearch(true)}>
                <SearchIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
        <Box sx={{ mb: 2, px: 2, py: 2 }}>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography variant="h5" fontWeight={600}>
              Founder
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  display: "flex",
                  flexDirection: { xs: "row", sm: "column", md: "row" },
                  alignItems: "center",
                  p: 3,
                  borderRadius: 3,
                  height: "100%",
                  border: "1px solid #e6e6e6",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 2,
                    overflow: "hidden",
                    mr: 2,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={getPublicS3Url(FOUNDER?.userImageURL) || dummyImage}
                    alt={FOUNDER.userFirstName + " " + FOUNDER.userLastName}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>

                <Box>
                  <Typography
                    fontWeight={700}
                    sx={{
                      mt: 0.5,
                      textAlign: { sm: "center", md: "left" },
                    }}
                  >
                    {FOUNDER.userFirstName + " " + FOUNDER.userLastName}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      textAlign: { sm: "center", md: "left" },
                    }}
                  >
                    {FOUNDER.position}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>

      {COMMITTEE_ORDER.map((committee) => {
        const membersList = groupedMembers[committee];
        if (!membersList || membersList.length === 0) return null;

        const style = getCommitteeStyle(committee);

        return (
          <Box
            key={committee}
            sx={{
              borderRadius: 4,
              mb: 4,
              px: 2,
              py: 2,
              background: style?.gradient,
            }}
          >
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h5" fontWeight={600}>
                {committee}
              </Typography>
            </Box>

            <Grid container spacing={3} justifyContent="center">
              {membersList.map((member) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={member._id || member?.userId}
                >
                  <Card
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "row", sm: "column", md: "row" },
                      alignItems: "center",
                      p: 3,
                      borderRadius: 3,
                      textOverflow: "scroll",
                      height: "100%",
                      border: "1px solid #e6e6e6",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      cursor: canOpenMemberProfile ? "pointer" : "default",
                      "&:hover": canOpenMemberProfile
                        ? {
                          boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
                        }
                        : {},
                    }}
                    onClick={() => {
                      const profileUserId = getMemberProfileUserId(member);
                      if (canOpenMemberProfile && profileUserId) {
                        setNetworkRestoreOnce();
                        navigate(`/profile/${profileUserId}`, {
                          state: {
                            mode: "view",
                            navigateFrom,
                            canOpenMemberProfile,
                          },
                        });
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: 2,
                        overflow: "hidden",
                        background: style?.solid,
                        mr: 2,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={getPublicS3Url(member?.userImageURL) || dummyImage}
                        alt={getFullName(member)}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>

                    <Box sx={{ justifyContent: "space-between" }}>
                      <Typography fontWeight={700} sx={{ mt: 0.5, textAlign: { sm: "center", md: "left" } }}>
                        {getFullName(member)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: { sm: "center", md: "left" } }}>
                        {member?.position}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Last updated: {formatUpdatedDate(visibleUpdatedAt)}
        </Typography>
      </Box>
    </Box>
  );
};

export default NEC;
